import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export interface CustomerTimelineActivity {
  id: string;
  /** ISO timestamp for the semantic time element. */
  dateTime: string;
  /** Date and time already formatted for display by the caller. */
  dateLabel: string;
  /** Decorative icon shown on the shared timeline rail. */
  icon: ReactNode;
  content: ReactNode;
}

export interface CustomerTimelineProps {
  activities: readonly CustomerTimelineActivity[];
  /** When present, dim activities outside this set while keeping their place on the rail. */
  highlightedActivityIds?: ReadonlySet<string>;
}

const fadeStyle =
  "transition-opacity duration-[350ms] motion-reduce:transition-none";
const railSegmentStyle = twMerge(
  "absolute left-1/2 w-px -translate-x-1/2 bg-border-strong",
  fadeStyle,
);

/** Owns the rail; activity contents remain composable. */
export const CustomerTimeline: FC<CustomerTimelineProps> = (props) => {
  const { activities, highlightedActivityIds } = props;
  const activeRows = activities.map(
    (activity) =>
      highlightedActivityIds === undefined ||
      highlightedActivityIds.has(activity.id),
  );
  // Keep the rail between highlighted nodes, even across dimmed rows.
  const firstActiveIndex = activeRows.indexOf(true);
  const lastActiveIndex = activeRows.lastIndexOf(true);

  return (
    <section aria-label="Customer timeline" className="min-w-0">
      <h2 className="mb-5 text-sm font-semibold text-foreground">Timeline</h2>

      {activities.length === 0 ? (
        <p className="py-8 text-sm text-muted-foreground">No activity yet.</p>
      ) : (
        <ol className="m-0 list-none p-0">
          {activities.map((activity, index) => {
            const isDimmed = !activeRows[index];
            const hasRailAbove =
              index > firstActiveIndex && index <= lastActiveIndex;
            const hasRailBelow =
              index >= firstActiveIndex && index < lastActiveIndex;

            return (
              <li
                key={activity.id}
                className="grid grid-cols-[2rem_minmax(0,1fr)] gap-x-2 pb-8 last:pb-0 sm:grid-cols-[7rem_3rem_minmax(0,1fr)] sm:gap-x-0"
              >
                <time
                  dateTime={activity.dateTime}
                  className={twMerge(
                    "col-start-2 row-start-1 mb-1 text-xs whitespace-nowrap text-muted-foreground sm:col-start-1 sm:mb-0 sm:pt-1",
                    fadeStyle,
                    isDimmed && "opacity-20",
                  )}
                >
                  {activity.dateLabel}
                </time>
                <div
                  aria-hidden="true"
                  className="relative col-start-1 row-span-2 row-start-1 flex justify-center sm:col-start-2 sm:row-span-1"
                >
                  {/* Each row draws its half of the rail on either side of
                      the node; the lower half crosses the row's bottom padding. */}
                  <span
                    className={twMerge(
                      railSegmentStyle,
                      "top-0 h-3",
                      !hasRailAbove && "opacity-0",
                    )}
                  />
                  <span
                    className={twMerge(
                      railSegmentStyle,
                      "top-3 -bottom-8",
                      !hasRailBelow && "opacity-0",
                    )}
                  />
                  <span
                    className={twMerge(
                      "relative flex size-6 shrink-0 scale-100 items-center justify-center rounded-full bg-gray-50 text-muted-foreground transition-[opacity,scale] duration-[350ms] motion-reduce:transition-none",
                      isDimmed && "scale-95 opacity-0",
                    )}
                  >
                    {activity.icon}
                  </span>
                </div>
                <div
                  className={twMerge(
                    "col-start-2 row-start-2 min-w-0 sm:col-start-3 sm:row-start-1",
                    fadeStyle,
                    isDimmed && "opacity-20",
                  )}
                >
                  {activity.content}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};
